import { Fragment, useContext, useEffect, useState } from "react";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NavItem from "./NavItem";
import DrawerHeader from "./DrawerHeader";
import { ReactComponent as FayeIcon } from "../../icons/FayeBrand.svg";
import { ReactComponent as ClipboardIcon } from "../../icons/Menu/Clipboard.svg";
import { ReactComponent as IdeaIcon } from "../../icons/Menu/Idea.svg";
import { ReactComponent as CalendarIcon } from "../../icons/Menu/Calendar.svg";
import { ReactComponent as HomeIcon } from "../../icons/Menu/Home.svg";
import { ReactComponent as GiftIcon } from "../../icons/Menu/Gift.svg";
import { ReactComponent as DocumentIcon } from "../../icons/Menu/Document.svg";
import { ReactComponent as InboxIcon } from "../../icons/Menu/Inbox.svg";
import { ReactComponent as LockIcon } from "../../icons/Menu/Lock.svg";
import CurrentUserAvatar from "../common/CurrentUserAvatar";
import LinkRouter from "../navigation/LinkRouter";
import { CurrentUserContext } from "../context/RequireAuth";
import { Box, Button, keyframes } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import useDigitalSubscriptionUX from "components/hooks/useDigitalSubscriptionUX";
import {
  CalendarClock,
  HandshakeIcon,
  Lightbulb,
  SquareChartGantt,
} from "lucide-react";

const giftColorCycle = keyframes`
  0%   { color: #F59E0B; }
  16%  { color: #EF4444; }
  33%  { color: #EC4899; }
  50%  { color: #8B5CF6; }
  66%  { color: #3B82F6; }
  83%  { color: #10B981; }
  100% { color: #F59E0B; }
`;

const pulseGlow = keyframes`
  0%, 82% { filter: none; color: inherit; }
  86% { filter: drop-shadow(0 0 3px rgba(250,204,21,0.4)); color: #d4a017; }
  90% { filter: drop-shadow(0 0 8px #facc15); color: #facc15; }
  94% { filter: drop-shadow(0 0 3px rgba(250,204,21,0.4)); color: #d4a017; }
  98%, 100% { filter: none; color: inherit; }
`;

const TWO_MINUTES = 2 * 60 * 1000;

const AnimatedLightbulb = () => {
  const [animate, setAnimate] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setAnimate(false), TWO_MINUTES);
    return () => clearTimeout(timer);
  }, []);
  return (
    <Box
      sx={{
        display: "flex",
        animation: animate
          ? `${pulseGlow} 10s ease-in-out infinite`
          : undefined,
      }}
    >
      <Lightbulb size={20} strokeWidth={1.5} />
    </Box>
  );
};

const AnimatedGiftIcon = () => (
  <Box
    sx={{ animation: `${giftColorCycle} 3s linear infinite`, display: "flex" }}
  >
    <GiftIcon />
  </Box>
);

const AddMemberTaskButton = ({}: {}) => {
  const navigate = useNavigate();
  const { loading, data } = useMemberDetails();
  const onClick = () => {
    const family = data?.family;
    if (family) {
      navigate("/tasks/add", { state: { family } });
    }
  };
  return (
    <Button
      variant="outlined"
      size="small"
      sx={{
        marginLeft: "16px",
        marginTop: "48px",
      }}
      onClick={onClick}
      disabled={loading || !data?.family}
    >
      New task
    </Button>
  );
};

type MemberDrawerContentProps = {
  isPersistent: boolean;
  selectedTab: string;
  open: boolean;
  setClosed(): void;
  isMobile: boolean;
};

export default ({
  isPersistent,
  selectedTab,
  open,
  setClosed,
}: MemberDrawerContentProps) => {
  const navigate = useNavigate();
  const currentUser = useContext(CurrentUserContext);
  const isDigitalSubscription = useDigitalSubscriptionUX();
  return (
    <Fragment>
      <List sx={{ padding: 0 }}>
        <DrawerHeader>
          {isPersistent && (
            <LinkRouter to="/">
              <FayeIcon
                style={{
                  height: "26px",
                  margin: "12px 0px",
                }}
              />
            </LinkRouter>
          )}
          {!isPersistent && (
            <IconButton
              onClick={() => setClosed()}
              sx={{ ml: "auto", marginRight: "10px" }}
            >
              <CloseIcon sx={{ color: "#545F71" }} />
            </IconButton>
          )}
        </DrawerHeader>
        <div style={{ paddingTop: "17px" }}>
          <NavItem
            selected={selectedTab === "/home"}
            open={open}
            onClick={setClosed}
            icon={<HomeIcon />}
            text="Home"
            url="/home"
          />
          {isDigitalSubscription && (
            <NavItem
              selected={selectedTab === "/documents"}
              open={open}
              onClick={setClosed}
              icon={<DocumentIcon />}
              text="Documents"
              url="/documents"
            />
          )}
          {!isDigitalSubscription && (
            <NavItem
              selected={selectedTab === "/activity"}
              open={open}
              onClick={setClosed}
              icon={<SquareChartGantt size={20} strokeWidth={1.5} />}
              text="Activity"
              url="/activity"
            />
          )}
          {isDigitalSubscription && (
            <NavItem
              selected={selectedTab === "/scheduled-reminders"}
              open={open}
              onClick={setClosed}
              icon={<CalendarClock size={20} strokeWidth={1.5} />}
              text="Routines"
              url="/scheduled-reminders"
            />
          )}
          {isDigitalSubscription && (
            <NavItem
              selected={selectedTab === "/delegations"}
              open={open}
              onClick={setClosed}
              icon={<HandshakeIcon size={20} strokeWidth={1.5} />}
              text="Helping hands"
              url="/delegations"
            />
          )}
          {isDigitalSubscription && (
            <NavItem
              selected={selectedTab === "/conversations"}
              open={open}
              onClick={setClosed}
              icon={<InboxIcon height="20px" width="20px" />}
              text="Conversations"
              url="/conversations"
            />
          )}
          {!isDigitalSubscription && (
            <NavItem
              selected={
                selectedTab === "/tasks" || selectedTab.startsWith("/tasks/")
              }
              open={open}
              onClick={setClosed}
              icon={<ClipboardIcon />}
              text="Tasks"
              url="/tasks"
            />
          )}
          {!isDigitalSubscription && (
            <NavItem
              selected={selectedTab === "/documents"}
              open={open}
              onClick={setClosed}
              icon={<DocumentIcon />}
              text="Documents"
              url="/documents"
            />
          )}
          <NavItem
            selected={
              selectedTab === (isDigitalSubscription ? "/access" : "/secrets")
            }
            open={open}
            onClick={setClosed}
            icon={<LockIcon />}
            text={isDigitalSubscription ? "Access" : "Secrets"}
            url={isDigitalSubscription ? "/access" : "/secrets"}
          />
          <NavItem
            selected={selectedTab === "/calendar"}
            open={open}
            onClick={setClosed}
            icon={<CalendarIcon />}
            text="Calendar"
            url="/calendar"
          />
          {!isDigitalSubscription && (
            <NavItem
              selected={selectedTab === "/task-discovery"}
              open={open}
              onClick={setClosed}
              icon={<IdeaIcon fill="#6B6E7B" />}
              text="Task discovery"
              url="/task-discovery"
            />
          )}
          {isDigitalSubscription && (
            <NavItem
              selected={selectedTab === "/inspiration"}
              open={open}
              onClick={setClosed}
              icon={<AnimatedLightbulb />}
              text="Inspiration"
              url="/inspiration"
            />
          )}
          {!isDigitalSubscription && (
            <NavItem
              selected={selectedTab === "/invite"}
              open={open}
              onClick={setClosed}
              icon={<AnimatedGiftIcon />}
              text="Invite friends"
              url="/invite"
            />
          )}
          {!isDigitalSubscription && <AddMemberTaskButton />}
          {isDigitalSubscription && (
            <Box
              sx={{
                marginTop: "48px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                variant="outlined"
                size="small"
                sx={{ color: "#3D3D3D", borderColor: "#3D3D3D" }}
                startIcon={<AnimatedGiftIcon />}
                onClick={() => {
                  setClosed();
                  navigate("/invite");
                }}
              >
                Invite friends
              </Button>
            </Box>
          )}
        </div>
      </List>
      <List>
        <NavItem
          selected={selectedTab === "/settings"}
          onClick={setClosed}
          open={open}
          icon={<CurrentUserAvatar size={48} clickable={false} />}
          text={currentUser.firstName}
          url="/settings"
        />
      </List>
    </Fragment>
  );
};
